import React, { useContext, useState, useMemo } from "react";
import PropTypes from "prop-types";
import "../styles/LineChart.scss";
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { GlobalContext } from "../context/GlobalContext";

// A set of default colors to cycle through if none are provided
const DEFAULT_COLORS = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff7300",
  "#a4de6c",
  "#d0ed57",
];

const LineChart = ({
  chartTitle,
  yAxisTitle,
  yAxisDomain,
  linesConfig,
}) => {
  const { state } = useContext(GlobalContext);
  const { rangeReleases } = state;
  const [hiddenLines, setHiddenLines] = useState({});

  // Format timestamp to readable date, formats the data to 2decimals only if float
const formattedData = useMemo(() => {
    return rangeReleases.map((entry) => {
      const newEntry = { ...entry };

      // Iterate through your line configurations to find keys that need formatting
      linesConfig.forEach((config) => {
        const rawValue = entry[config.key];
        const numValue = parseFloat(rawValue);

        // Check if it's a valid number and if it actually has decimals (is a float)
        if (!isNaN(numValue) && numValue % 1 !== 0) {
          newEntry[config.key] = parseFloat(numValue.toFixed(2));
        } else if (!isNaN(numValue)) {
          // If it's an integer, ensure it stays a number type
          newEntry[config.key] = numValue;
        }
      });

      // Keep your existing date formatting
      newEntry.formattedDate = new Date(entry.CreationTimestamp).toLocaleDateString(
        "en-GB",
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }
      );

      return newEntry;
    });
  }, [rangeReleases, linesConfig]);

  const xTicks = formattedData.map((d) => d.formattedDate);

  const formatWithSpaces = (value) =>
    String(value).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  // Toggle line visibility when legend item is clicked
  const handleLegendClick = (e) => {
    const { dataKey } = e;
    setHiddenLines((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  return (
    <div className="lc-main-container">
      <span className="lc-title">{chartTitle}</span>

      <ResponsiveContainer width="100%" height={300}>
        <RechartsLineChart
          data={formattedData}
          margin={{ top: 20, right: 30, left: 30, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="rgba(255,255,255,0.1)"
          />

          <XAxis
            dataKey="formattedDate"
            interval={Math.max(0, Math.ceil(formattedData.length / 14) - 1)}
            stroke="#fff"
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={60}
            allowDuplicatedCategory={false}
          />

          <YAxis
            stroke="#fff"
            domain={yAxisDomain || ["auto", "auto"]}
            tickCount={4}
            allowDecimals
            tickFormatter={formatWithSpaces}
            tick={{ fontSize: 12 }}
            label={
              yAxisTitle
                ? {
                    value: yAxisTitle,
                    angle: -90,
                    position: "insideLeft",
                    fill: "#fff",
                    style: { textAnchor: "middle" },
                  }
                : undefined
            }
          />

 <Tooltip
  contentStyle={{
    backgroundColor: "rgba(35, 50, 70, 0.8)", // Slightly more opaque for readability
    border: "1px solid var(--purple)",
    borderRadius: "4px",
    color: "#fff",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
  }}
  formatter={(value, name) => [
    value !== null && value !== undefined ? formatWithSpaces(value) : "N/A",
    name,
  ]}
/>

          <Legend
            verticalAlign="top"
            height={36}
            onClick={handleLegendClick}
          />

          {linesConfig.map((line, idx) => (
            <Line
              key={line.key}
              type="monotone"
              dataKey={line.key}
              name={line.name || line.key}
              stroke={
                line.color ||
                DEFAULT_COLORS[idx % DEFAULT_COLORS.length]
              }
              strokeWidth={2}
              dot={{
                r: 6,
                fill:
                  line.dotColor ||
                  DEFAULT_COLORS[idx % DEFAULT_COLORS.length],
              }}
              hide={!!hiddenLines[line.key]}
            />
          ))}
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>
  );
};

LineChart.propTypes = {
  chartTitle: PropTypes.string.isRequired,
  yAxisTitle: PropTypes.string,
  yAxisDomain: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  linesConfig: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string,
      color: PropTypes.string,
      dotColor: PropTypes.string,
    })
  ),
};

LineChart.defaultProps = {
  linesConfig: [],
  yAxisTitle: "",
  yAxisDomain: null,
};

export default LineChart;
